import {NgTemplateOutlet} from "@angular/common"
import {Component, computed, DestroyRef, inject, Injector, input, OnInit, output, signal, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_TEMPLATES} from "@common/services/logging"
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterLink} from "@angular/router"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {ImageProcessingService} from "@app/common/services/rendering/image-processing.service"
import {RenderingService} from "@app/common/services/rendering/rendering.service"
import {
    createRenderJob,
    getAssignmentKey,
    getConfigurationString,
    getJobAssignments,
    getPostProcessingInput,
    isJobError,
    isJobPending,
    JobData,
    postProcessingSettingsToImageProcessingSettings,
} from "@app/template-editor/helpers/template-jobs"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ImageProcessingNodes, PostProcessingInputData} from "@cm/image-processing-nodes"
import {postProcessingGraph} from "@cm/image-processing/operations/render-post-processing"
import {PictureRenderJobOutput, PictureRenderJobOutputSchema} from "@cm/job-nodes/rendering"
import {findLayerPlanContext, LayerPlanContext, LayerPlanLayer, SceneNodes} from "@cm/template-nodes"
import {activeSolo, layerLabel, stackLayers, submitLayerRenderJobs, sumLayers} from "@app/template-editor/helpers/layer-jobs"
import {TemplateJobIconComponent} from "@app/template-editor/components/template-job-icon/template-job-icon.component"
import {hashObject} from "@cm/utils"
import {jsonToGraph} from "@cm/browser-utils"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {LoadingSpinnerComponent, LoadingSpinnerIconComponent} from "@common/components/progress"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {LocalStorageService} from "@common/services/local-storage/local-storage.service"
import {MatSelectModule} from "@angular/material/select"
import {MatFormFieldModule} from "@angular/material/form-field"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {ImageColorSpace, JobState} from "@generated"
import {
    CancelJobForTemplateImageViewerGQL,
    DiscardJobTaskForTemplateImageViewerGQL,
    GetJobDetailsForTemplateImageViewerGQL,
    GetJobsForTemplateImageViewerGQL,
    JobDetailsForTemplateImageViewerFragment,
} from "@template-editor/components/template-image-viewer/template-image-viewer.component.generated"
import {TemplateJobManagerService} from "@template-editor/services/template-job-manager.service"
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    defer,
    EMPTY,
    filter,
    firstValueFrom,
    from,
    map,
    Observable,
    of,
    switchMap,
    takeWhile,
    tap,
    distinctUntilChanged,
    timer,
} from "rxjs"
import {
    ImageViewState,
    TemplateImageViewerCanvasComponent,
} from "@app/template-editor/components/template-image-viewer-canvas/template-image-viewer-canvas.component"
import {TemplateImageViewerControlsComponent} from "@app/template-editor/components/template-image-viewer-controls/template-image-viewer-controls.component"
import {TemplateTreeViewType} from "@app/template-editor/components/template-tree-view-type-selector/template-tree-view-type-selector.component"
import {ActionItemComponent} from "@app/common/components/menu/actions/action-item/action-item.component"
import {ImageProcessingUtils} from "@cm/image-processing/image-processing"
import {environment} from "@environment"
import {SceneManagerCacheService} from "@app/template-editor/services/scene-manager-cache.service"

const JOB_POLL_INTERVAL = 5000 // 5 seconds

type StackTarget = {kind: "stack"; context: LayerPlanContext; layers: LayerPlanLayer[]}
type LayerTarget = {kind: "classifying"} | {kind: "outside"} | {kind: "layer"; context: LayerPlanContext; layer: LayerPlanLayer} | StackTarget

function layerTargetKey(target: LayerTarget | undefined) {
    if (!target) return ""
    if (target.kind === "layer") return `layer ${target.layer.key}`
    if (target.kind === "stack") return `stack ${target.layers.map((layer) => layer.key).join(" ")}`
    return target.kind
}

type ErrorResult = {error: {description: string}}
type StackJobs = {stackJobs: JobData[]}
type RenderSource = {renderOutputs: PictureRenderJobOutput[]}

export type PostProcessedImageData = {
    imageData: ImageData
    rawImageData: PostProcessingInputData
}
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-image-viewer",
    templateUrl: "./template-image-viewer.component.html",
    styleUrl: "./template-image-viewer.component.scss",
    providers: [SceneManagerService],
    imports: [
        LoadingSpinnerComponent,
        TemplateImageViewerCanvasComponent,
        TemplateImageViewerControlsComponent,
        ButtonComponent,
        MatTooltipModule,
        LoadingSpinnerIconComponent,
        ActionItemComponent,
        MatMenuModule,
        RouterLink,
        NgTemplateOutlet,
        MatSelectModule,
        MatFormFieldModule,
        TemplateJobIconComponent,
    ],
})
export class TemplateImageViewerComponent implements OnInit {
    readonly $maskOverlayColor = input<ImageProcessingNodes.RGBColor>([1, 0.2, 0.2], {alias: "maskOverlayColor"})
    readonly $maskOverlayAlpha = input(0.5, {alias: "maskOverlayAlpha"})
    readonly $fileTitle = input<string>(undefined, {alias: "fileTitle"})
    readonly $viewType = input.required<TemplateTreeViewType>({alias: "viewType"})

    readonly $sceneManagerService = input.required<SceneManagerService>({alias: "sceneManagerService"})
    private readonly sceneManagerCacheService = inject(SceneManagerCacheService)
    private downloadAbortController: AbortController | null = null
    private readonly localSceneManagerService = inject(SceneManagerService)
    private readonly injector = inject(Injector)
    private readonly destroyRef = inject(DestroyRef)
    private readonly renderingService = inject(RenderingService)
    private readonly notificationsService = inject(NotificationsService)
    private readonly templateJobManagerService = inject(TemplateJobManagerService)
    private readonly getJobDetailsForTemplateImageViewerGQL = inject(GetJobDetailsForTemplateImageViewerGQL)
    private readonly cancelJobGql = inject(CancelJobForTemplateImageViewerGQL)
    private readonly discardJobTaskGql = inject(DiscardJobTaskForTemplateImageViewerGQL)
    private readonly jobsGql = inject(GetJobsForTemplateImageViewerGQL)
    private readonly localStorageService = inject(LocalStorageService)

    readonly developmentMode = this.localStorageService.developmentMode
    readonly $renderQueueGroups = this.templateJobManagerService.$renderQueueGroups
    readonly $renderQueueGroup = this.templateJobManagerService.$renderQueueGroup
    private readonly logger = inject(CM_LOGGER_TEMPLATES)
    readonly $can = inject(PermissionsService).$to

    private readonly $postProcessingSettings = computed(() => this.$sceneManagerService().$scene().find(SceneNodes.RenderPostProcessingSettings.is))

    private resolvedProcessingSettings$ = toObservable(this.$postProcessingSettings).pipe(
        map((settings) => (settings ? postProcessingSettingsToImageProcessingSettings(settings) : undefined)),
        switchMap((settings) => {
            if (!settings) return of(undefined)

            function isColorTuple(x: ImageProcessingNodes.RGBColor | ImageProcessingNodes.ImageNode | undefined): x is ImageProcessingNodes.RGBColor {
                return Array.isArray(x) && x.length === 3 && x.every((num) => typeof num === "number")
            }

            const {backgroundColor, mask, ...rest} = settings

            const resolveImage = (image: ImageProcessingNodes.ImageNode) =>
                defer(() =>
                    from(
                        ImageProcessingUtils.resolveExternalData(image, async (node) => {
                            if (node.sourceData.type !== "dataObjectReference") throw new Error("Unsupported source type")
                            if (typeof node.resolvedData !== "undefined") return
                            if (node.resolveTo !== "encodedData") throw new Error(`Cannot resolve external data type: ${node.resolveTo}`)

                            const {dataObject, dataObjectLegacy} = this.sceneManagerCacheService.dataResolverService.resolve
                            const {dataObjectId} = node.sourceData

                            const resolvedDataObject = await (typeof dataObjectId === "number" ? dataObjectLegacy(dataObjectId) : dataObject(dataObjectId))

                            const resp = await firstValueFrom(this.sceneManagerCacheService.dataObjectContentCache.evaluateDataObject(resolvedDataObject))

                            node.resolvedData = {
                                data: resp,
                                mediaType: resolvedDataObject.mediaType,
                                colorSpace: resolvedDataObject.imageColorSpace === ImageColorSpace.Srgb ? "sRGB" : "linear",
                            }
                        }),
                    ),
                )

            return combineLatest([
                isColorTuple(backgroundColor) || !backgroundColor ? of(backgroundColor) : resolveImage(backgroundColor),
                mask ? resolveImage(mask) : of(undefined),
            ]).pipe(map(([backgroundColor, mask]) => ({...rest, backgroundColor, mask})))
        }),
    )
    private maskOverlayColor$ = toObservable(this.$maskOverlayColor)
    private maskOverlayAlpha$ = toObservable(this.$maskOverlayAlpha)

    readonly $renderSettings = computed(() => this.$sceneManagerService().$scene().find(SceneNodes.RenderSettings.is))
    readonly $camera = computed(() => this.$sceneManagerService().$scene().find(SceneNodes.Camera.is))

    readonly viewTypeChanged = output<TemplateTreeViewType>()

    readonly $imageViewState = signal<ImageViewState | undefined>(undefined)
    readonly $stage = signal<"init" | "waitingRenderOutput" | "downloading" | "processing" | "done">("init")
    readonly $renderJobData = signal<JobData | undefined>(undefined)
    readonly $result = signal<{error: {description: string}} | PostProcessedImageData | undefined>(undefined)
    readonly $startingRenderJob = signal(false)
    readonly $cancellingRenderJob = signal(false)
    readonly $deletingRenderJob = signal(false)

    $overrideRenderJobData = this.templateJobManagerService.$selectedUnassignedJob
    overrideRenderJobData$ = toObservable(this.$overrideRenderJobData)

    readonly $currentConfiguration = computed(() => this.$sceneManagerService().$currentLocalConfiguration())
    currentConfiguration$ = toObservable(this.$currentConfiguration)

    readonly $layerTarget = computed(
        (): LayerTarget | undefined => {
            if (!this.templateJobManagerService.$layerMode()) return undefined
            const plan = this.templateJobManagerService.$layerPlan()
            if (!plan) return {kind: "classifying"}
            const configuration = this.$currentConfiguration().parameters
            const context = findLayerPlanContext(plan, configuration)
            if (!context) return {kind: "outside"}
            const solo = activeSolo(plan, this.templateJobManagerService.$soloLayer(), configuration)
            if (solo) return {kind: "layer", context, layer: solo.layer}
            return {kind: "stack", context, layers: stackLayers(context, configuration)}
        },
        {equal: (a, b) => layerTargetKey(a) === layerTargetKey(b)},
    )
    readonly $compositeReady = signal(false)
    private readonly $stackIncomplete = signal(false)
    readonly $layerOverview = computed(() => {
        const target = this.$layerTarget()
        if (!target || target.kind === "layer" || this.$overrideRenderJobData()) return undefined
        return target.kind !== "stack" || (this.$stackIncomplete() && !this.$compositeReady()) ? target : undefined
    })
    readonly $compositeLabel = computed(() => {
        const target = this.$layerTarget()
        if (target?.kind !== "stack" || !this.$compositeReady()) return undefined
        return {count: target.layers.length, tooltip: target.layers.map(layerLabel).join(" + ")}
    })
    readonly $soloLabel = computed(() => {
        const target = this.$layerTarget()
        return target?.kind === "layer" ? layerLabel(target.layer) : undefined
    })
    readonly $stackEntries = computed(() => {
        const target = this.$layerTarget()
        if (target?.kind !== "stack") return []
        const jobs = this.templateJobManagerService.$layerJobs()
        return target.layers.map((layer) => ({context: target.context, layer, label: layerLabel(layer), job: jobs.get(layer.key)}))
    })
    readonly $missingStackLayers = computed(() => this.$stackEntries().filter((entry) => !entry.job))
    readonly $failedStackLayers = computed(() => this.$stackEntries().filter((entry) => entry.job && isJobError(entry.job)))

    JobState = JobState
    readonly $isJobPending = signal(isJobPending)
    readonly $isJobError = signal(isJobError)

    private refreshJobs$ = new BehaviorSubject<void>(undefined)

    refreshJobs() {
        this.refreshJobs$.next()
    }

    private readonly imageProcessingService = inject(ImageProcessingService)

    isError = (result: unknown): result is {error: {description: string}} => {
        if (result === null || typeof result !== "object") return false
        if (!("error" in result)) return false
        return typeof result.error !== "undefined"
    }

    ngOnInit() {
        this.templateJobManagerService.jobsDiscarded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refreshJobs())
        this.localSceneManagerService.$lodType.set("pathTraced")
        this.localSceneManagerService.$computeDummyReifiedMeshData.set(true)

        if (this.developmentMode) void this.templateJobManagerService.loadRenderQueueGroups()

        const handleError = (error: unknown) => {
            return of({error: {description: error instanceof Error ? error.message : String(error)}})
        }

        const settings$ = combineLatest([this.resolvedProcessingSettings$, this.maskOverlayColor$, this.maskOverlayAlpha$]).pipe(
            map(([postProcessingSettings, maskOverlayColor, maskOverlayAlpha]) => ({postProcessingSettings, maskOverlayColor, maskOverlayAlpha})),
        )

        const jobChanged$ = combineLatest([
            this.overrideRenderJobData$,
            this.$sceneManagerService().templateRevisionId$,
            this.currentConfiguration$,
            this.refreshJobs$.pipe(map((_, index) => index)),
            toObservable(this.$layerTarget, {injector: this.injector}),
        ]).pipe(
            // With layers the image depends on the target, not on the configuration: other options of the stack can change under a soloed layer.
            distinctUntilChanged(
                (
                    [previousOverride, previousRevision, previousConfiguration, previousRefresh, previousTarget],
                    [override, revision, configuration, refresh, target],
                ) =>
                    previousOverride === override &&
                    previousRevision === revision &&
                    previousRefresh === refresh &&
                    (target
                        ? layerTargetKey(previousTarget) === layerTargetKey(target)
                        : !previousTarget && getConfigurationString(previousConfiguration) === getConfigurationString(configuration)),
            ),
        )

        jobChanged$
            .pipe(
                tap(() => {
                    this.$stage.set("init")
                    this.$renderJobData.set(undefined)
                    this.$compositeReady.set(false)
                    this.$stackIncomplete.set(false)
                }),
                switchMap(([overrideRenderJobData, templateRevisionId, configuration, , layerTarget]): Observable<JobData | StackJobs | ErrorResult> => {
                    if (overrideRenderJobData) return of(overrideRenderJobData.job)

                    if (layerTarget && layerTarget.kind !== "layer") {
                        this.$result.set(undefined)
                        if (layerTarget.kind !== "stack" || !templateRevisionId) {
                            this.$stage.set("done")
                            return EMPTY
                        }
                        return this.finishedStackJobs(templateRevisionId, layerTarget).pipe(catchError(handleError))
                    }

                    if (!templateRevisionId) return handleError(Error("No template revision id set"))

                    const assignmentKey = layerTarget ? layerTarget.layer.key : getAssignmentKey("render", getConfigurationString(configuration))
                    return from(getJobAssignments(this.injector, templateRevisionId, [assignmentKey])).pipe(
                        map(([renderJob]) => {
                            if (!renderJob)
                                throw Error(layerTarget ? "No render job found for this layer" : "No render job found for the current configuration")
                            return renderJob
                        }),
                        catchError(handleError),
                    )
                }),
                switchMap((source): Observable<RenderSource | ErrorResult> => {
                    if (this.isError(source)) return of(source)
                    if ("stackJobs" in source) {
                        this.$compositeReady.set(true)
                        return of({renderOutputs: source.stackJobs.map((job) => PictureRenderJobOutputSchema.parse(jsonToGraph(job.output)))})
                    }
                    this.$renderJobData.set(source)
                    const updates: Observable<JobData | ErrorResult> = isJobPending(source)
                        ? this.getJobDetailsForTemplateImageViewerGQL
                              .watch({id: source.id}, {pollInterval: JOB_POLL_INTERVAL, fetchPolicy: "no-cache"})
                              .valueChanges.pipe(
                                  map(({data}): JobDetailsForTemplateImageViewerFragment => data.job),
                                  takeWhile((job) => isJobPending(job), true),
                                  catchError(handleError),
                              )
                        : of(source)
                    return updates.pipe(
                        switchMap((renderJob) => {
                            try {
                                if (this.isError(renderJob)) return of(renderJob)
                                this.$renderJobData.set(renderJob)
                                if (isJobError(renderJob)) throw Error(`Render job failed with state ${renderJob.state} and message: ${renderJob.message}`)
                                if (!isJobPending(renderJob) && renderJob.state !== JobState.Complete)
                                    throw Error(`Render job in unexpected state ${renderJob.state}`)
                                const renderOutput = PictureRenderJobOutputSchema.parse(jsonToGraph(renderJob.output))
                                if (!renderOutput || (!renderOutput.renderPasses && !renderOutput.preview)) {
                                    if (isJobPending(renderJob)) {
                                        this.$stage.set("waitingRenderOutput")
                                        return EMPTY
                                    }
                                    throw Error("Render output has no render passes or preview")
                                }
                                return of({renderOutputs: [renderOutput]})
                            } catch (error) {
                                return handleError(error)
                            }
                        }),
                        distinctUntilChanged((prev, curr) => {
                            if (this.isError(curr) || this.isError(prev)) return false
                            return hashObject(prev.renderOutputs) === hashObject(curr.renderOutputs)
                        }),
                    )
                }),
                tap(() => {
                    // Abort any previous download before starting a new one
                    if (this.downloadAbortController) {
                        this.downloadAbortController.abort()
                    }
                    this.downloadAbortController = new AbortController()
                    this.$stage.set("downloading")
                }),
                switchMap((data) => {
                    if (this.isError(data)) return of(data)
                    const abortSignal = this.downloadAbortController!.signal
                    return from(Promise.all(data.renderOutputs.map((output) => getPostProcessingInput(output, this.injector, true, abortSignal)))).pipe(
                        map(([first, ...rest]) => ({postProcessingInput: rest.length > 0 ? sumLayers(first, rest) : first})),
                        catchError(handleError),
                    )
                }),
                switchMap((data) => {
                    if (this.isError(data)) return of(data)
                    const {postProcessingInput} = data

                    return settings$.pipe(
                        tap(() => {
                            this.$stage.set("processing")
                        }),
                        switchMap(({postProcessingSettings, maskOverlayColor, maskOverlayAlpha}) => {
                            const solo = this.$layerTarget()?.kind === "layer"
                            const settings = solo ? {...postProcessingSettings, composite: false} : (postProcessingSettings ?? {})
                            const {image, selectedMask: mask} = postProcessingGraph(postProcessingInput, settings)
                            let graph = image

                            if (mask) {
                                graph = {
                                    type: "blend",
                                    mode: "normal",
                                    amount: maskOverlayAlpha,
                                    background: graph,
                                    foreground: {
                                        type: "applyMask",
                                        input: {
                                            type: "sRGB",
                                            color: maskOverlayColor,
                                        },
                                        mask,
                                    },
                                }
                            }
                            graph = {
                                type: "convert",
                                dataType: "uint8",
                                channelLayout: "RGBA",
                                sRGB: true,
                                input: graph,
                            }

                            return this.imageProcessingService.evalGraph(graph).pipe(catchError(handleError))
                        }),
                        switchMap((processedImage) => {
                            if (this.isError(processedImage)) return of(processedImage)
                            return this.imageProcessingService.toCanvasImageData(processedImage.image)
                        }),
                        filter((imageData): imageData is ImageData => {
                            if (imageData === null) throw new Error("Image data is null")
                            return true
                        }),
                        map((imageData) => ({imageData, rawImageData: postProcessingInput})),
                        catchError(handleError),
                    )
                }),
                catchError(handleError),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((result) => {
                this.$stage.set("done")
                this.$result.set(result)
            })
    }

    // The panel's status icons read the shared layer jobs, so the polled states are written back there.
    private finishedStackJobs(templateRevisionId: string, target: StackTarget): Observable<StackJobs> {
        const ordered = [...target.layers].sort((a, b) => Number(!!a.group) - Number(!!b.group))
        const keys = ordered.map((layer) => layer.key)
        return timer(0, JOB_POLL_INTERVAL).pipe(
            switchMap(() => from(getJobAssignments(this.injector, templateRevisionId, keys))),
            tap((jobs) =>
                this.templateJobManagerService.$layerJobs.update((current) => {
                    const next = new Map(current)
                    keys.forEach((key, index) => {
                        const job = jobs[index]
                        if (job) next.set(key, job)
                        else next.delete(key)
                    })
                    return next
                }),
            ),
            takeWhile((jobs) => jobs.some((job) => job !== undefined && isJobPending(job)), true),
            filter((jobs): jobs is JobData[] => {
                const finished = jobs.every((job) => job?.state === JobState.Complete)
                if (!finished && !this.$stackIncomplete()) {
                    this.$stackIncomplete.set(true)
                    this.$stage.set("done")
                }
                return finished
            }),
            map((jobs) => ({stackJobs: jobs})),
        )
    }

    private async prepareRender(newJobs: number) {
        const templateRevisionId = this.$sceneManagerService().$templateRevisionId()
        if (!templateRevisionId) throw Error("No template revision id set")

        const defaultCustomerId = this.$sceneManagerService().$defaultCustomerId()
        if (!defaultCustomerId) throw Error("No default customer id set")

        const runningJobs = await fetchThrowingErrors(this.jobsGql)({
            filter: {
                organizationLegacyId: {equals: defaultCustomerId},
                state: [JobState.Init, JobState.Runnable, JobState.Running],
            },
        })

        if (runningJobs.jobs.length + newJobs - 1 > environment.rendering.maxConcurrentRenderJobs)
            throw new Error("Too many concurrent render jobs. Please wait for existing jobs to complete before starting a new one.")

        this.localSceneManagerService.$templateRevisionId.set(templateRevisionId)
        this.localSceneManagerService.$defaultCustomerId.set(defaultCustomerId)

        const clonedTemplateGraph = this.$sceneManagerService()
            .$templateGraph()
            .clone({cloneSubNode: () => true})
        this.localSceneManagerService.$templateGraph.set(clonedTemplateGraph)
        return {templateRevisionId, defaultCustomerId}
    }

    async render() {
        const target = this.$layerTarget()
        if (target?.kind === "layer") return this.renderLayers(target.context, [target.layer])
        try {
            this.$startingRenderJob.set(true)

            const {templateRevisionId, defaultCustomerId} = await this.prepareRender(1)

            const clonedConfiguration = this.$currentConfiguration().clone({cloneSubNode: () => true})
            this.localSceneManagerService.$instanceParameters.set(clonedConfiguration)

            this.localSceneManagerService.compileTemplate()
            await this.localSceneManagerService.syncSolved(true)

            const variationString = getConfigurationString(clonedConfiguration)

            const [, renderJob] = await createRenderJob(
                this.injector,
                this.renderingService,
                templateRevisionId,
                variationString,
                this.localSceneManagerService.$scene(),
                defaultCustomerId,
                this.$renderQueueGroup() || undefined,
            )

            this.notificationsService.showInfo(`Job submitted. (id = ${renderJob.id})`)
        } catch (error) {
            this.notificationsService.showError(`Failed to start render job: ${(error as Error).message}`)
            this.logger.error("Failed to start render job", error)
        } finally {
            this.$startingRenderJob.set(false)
        }

        this.refreshJobs()
    }

    renderMissingStackLayers() {
        const target = this.$layerTarget()
        if (target?.kind !== "stack") return
        return this.renderLayers(
            target.context,
            this.$missingStackLayers().map((entry) => entry.layer),
        )
    }

    private async renderLayers(context: LayerPlanContext, layers: LayerPlanLayer[]) {
        if (layers.length === 0) return
        try {
            this.$startingRenderJob.set(true)
            const {templateRevisionId, defaultCustomerId} = await this.prepareRender(layers.length)
            const {submitted, failed} = await submitLayerRenderJobs({
                injector: this.injector,
                renderingService: this.renderingService,
                sceneManagerService: this.localSceneManagerService,
                templateRevisionId,
                organizationLegacyId: defaultCustomerId,
                queueGroup: this.$renderQueueGroup() || undefined,
                context,
                layers,
                onSubmitted: (layer, job) => this.templateJobManagerService.$layerJobs.update((jobs) => new Map(jobs).set(layer.key, job)),
                onError: (_layer, error) => this.logger.error("Failed to start a layer render job", error),
            })
            if (failed > 0) this.notificationsService.showError(`Submitted ${submitted} layer render jobs, ${failed} failed.`)
            else this.notificationsService.showInfo(`Submitted ${submitted} layer render ${submitted === 1 ? "job" : "jobs"}.`)
        } catch (error) {
            this.notificationsService.showError(`Failed to start layer render jobs: ${(error as Error).message}`)
            this.logger.error("Failed to start layer render jobs", error)
        } finally {
            this.$startingRenderJob.set(false)
        }

        this.refreshJobs()
    }

    soloLayer(context: LayerPlanContext, layer: LayerPlanLayer) {
        this.templateJobManagerService.$soloLayer.set({context, layer})
    }

    endSolo() {
        this.templateJobManagerService.$soloLayer.set(undefined)
    }

    async reRender() {
        // Retire the current render first so its slot is free, then submit the new one. If retiring
        // fails, stop: the slot is still taken, and the error has already been shown.
        if (!(await this.discardRenderJob())) return
        await this.render()
    }

    // Stop a running render. A plain cancel, not a discard: a render that finished meanwhile is
    // refused by the backend and kept, and a cancelled render can still be restarted.
    async cancelRenderJob() {
        this.$cancellingRenderJob.set(true)
        try {
            const job = this.$overrideRenderJobData()?.job ?? this.$renderJobData()
            if (job) await mutateThrowingErrors(this.cancelJobGql)({id: job.id})
        } catch (error) {
            this.notificationsService.showError(`Failed to cancel render job: ${(error as Error).message}`)
            this.logger.error("Failed to cancel render job", error)
        } finally {
            this.$cancellingRenderJob.set(false)
            this.refreshJobs()
        }
    }

    // Retire the shown render (assigned or unassigned) instead of deleting it: the job stays on
    // record and its slot frees. Returns whether the slot is free afterwards — also true when there
    // was nothing to retire. On failure the view is left as it was, since the job is still there.
    async discardRenderJob(): Promise<boolean> {
        this.$deletingRenderJob.set(true)
        const override = this.$overrideRenderJobData()
        try {
            const job = override?.job ?? this.$renderJobData()
            if (job) await mutateThrowingErrors(this.discardJobTaskGql)({id: job.id})
            const target = this.$layerTarget()
            if (!override && target?.kind === "layer") {
                this.templateJobManagerService.$layerJobs.update((jobs) => {
                    const next = new Map(jobs)
                    next.delete(target.layer.key)
                    return next
                })
            }
            if (override)
                this.$overrideRenderJobData.set(undefined) // this triggers refreshJobs
            else this.refreshJobs()
            return true
        } catch (error) {
            this.notificationsService.showError(`Failed to discard render job: ${(error as Error).message}`)
            this.logger.error("Failed to discard render job", error)
            this.refreshJobs()
            return false
        } finally {
            this.$deletingRenderJob.set(false)
        }
    }
}
